import { Stack, Tag } from '@chakra-ui/react';
import { CalendarDaysIcon, UserIcon } from '@heroicons/react/24/outline';
import { Field, Text } from '@main/complykit';
import { getQuestionnaireType, QUESTIONNAIRE_TYPES, QuestionnaireType } from '@main/shared/browser';
import { formatDate } from '@main/shared/utils';
import {
  DrawerProperty,
  DrawerPropertyMarkdown,
  EditableAvatar,
  FileUpload,
  useDownloadStorageFile,
  useLazyFileUpload,
} from '@main/ui';
import { BarsIcon, TagIcon } from '@main/ui/icons';
import { useTranslation } from 'react-i18next';

import { useGetVendorQuestionnaireDrawerQuery } from './drawer.vendor.generated';

export interface VendorQuestionnaireDetailsTabProps {
  questionnaireId: string;
}

export function VendorQuestionnaireDetailsTab({
  questionnaireId,
}: VendorQuestionnaireDetailsTabProps) {
  const { t } = useTranslation();
  const downloadStorageFile = useDownloadStorageFile();
  const { data } = useGetVendorQuestionnaireDrawerQuery({ questionnaireId });

  const questionnaire = data?.vendor_questionnaires_by_pk;

  const questionnaireFileUpload = useLazyFileUpload({
    file: questionnaire?.questionnaire.file ?? undefined,
    onFileDownload: downloadStorageFile,
  });

  if (!questionnaire) {
    return null;
  }

  const type = getQuestionnaireType(questionnaire.questionnaire);
  const typeMeta = QUESTIONNAIRE_TYPES[type];
  const vendorName = questionnaire.vendor?.name;

  return (
    <Stack spacing={2.5}>
      {questionnaire.questionnaire.description && (
        <DrawerProperty isReadOnly>
          <DrawerProperty.Label icon={BarsIcon}>
            {t('vendors.questionnaires.tabs.details.description')}
          </DrawerProperty.Label>
          <DrawerProperty.Content>
            <DrawerPropertyMarkdown defaultValue={questionnaire.questionnaire.description} />
          </DrawerProperty.Content>
        </DrawerProperty>
      )}

      <DrawerProperty>
        <DrawerProperty.Label icon={TagIcon}>
          {t('vendors.questionnaires.tabs.details.type')}
        </DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          <Tag colorScheme={typeMeta.colorScheme} w="fit-content" data-testid="questionnaire-type">
            {t(typeMeta.value)}
          </Tag>
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={BarsIcon}>
          {t('vendors.questionnaires.tabs.details.sentTo')}
        </DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          <Text size="xs">{vendorName}</Text>
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={UserIcon}>
          {t('vendors.questionnaires.tabs.details.sentBy')}
        </DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          <EditableAvatar.Option {...questionnaire.sent_by_user}></EditableAvatar.Option>
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={CalendarDaysIcon}>
          {t('vendors.questionnaires.tabs.details.sentDate')}
        </DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          <Text size="xs">{formatDate(questionnaire.created_at)}</Text>
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={CalendarDaysIcon}>
          {t('vendors.questionnaires.tabs.details.dueDate')}
        </DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          <Text size="xs">{formatDate(questionnaire.due_date)}</Text>
        </DrawerProperty.Content>
      </DrawerProperty>

      {type === QuestionnaireType.File && (
        // Field park: the file list sits in the field anatomy until the upload round's file field.
        <Field label={t('vendors.questionnaires.tabs.details.file')} isReadOnly>
          <FileUpload {...questionnaireFileUpload.props} isReadOnly>
            <FileUpload.Files />
          </FileUpload>
        </Field>
      )}
    </Stack>
  );
}
